import type { PrivacyData } from './index'

/**
 * English Privacy Notice content.
 *
 * Returns a GitHub-Flavored Markdown string with contact data injected
 * from the supplied `PrivacyData` object.  The caller (getPrivacyContent)
 * passes `privacyData`; future language files follow the same signature.
 *
 * The heading "Privacy Notice" (h1) is rendered separately by PrivacyPage
 * as the `.staticPageTitle`; content starts after it.
 */
export function getEnPrivacyMarkdown(data: PrivacyData): string {
  const { lastUpdated, publicationName, responsiblePerson, email, address, country } = data
  const addressMd = [...address, country].join('  \n')

  return `**Last updated: ${lastUpdated}**

---

## 1. Who We Are

${publicationName} is an online newspaper. We are the **data controller** responsible for the personal data described in this notice.

**Contact:**  
**${publicationName}**  
${addressMd}

Email: [${email}](mailto:${email})

If you have any questions about this Privacy Notice or about how we handle your personal data, please contact us at the address above.

---

## 2. What Personal Data We Collect, Why, and on What Legal Basis

We collect and process personal data only to the minimum extent necessary to operate our service. We do **not** track users, display advertising, or share data with third parties for commercial purposes.

### 2.1 Account Data (Registration and Login)

When you create an account, we collect:

| Data | Purpose | Legal Basis |
|---|---|---|
| Chosen username | To identify your account | Performance of a contract (Art. 6(1)(b) GDPR) |
| Email address (verified) | To authenticate your account and contact you about your account | Performance of a contract (Art. 6(1)(b) GDPR) |
| Password or passkey | To authenticate your identity securely | Performance of a contract (Art. 6(1)(b) GDPR) |

This data is **strictly necessary** to provide you with a secure account. Without it, we cannot offer you a registered account.

### 2.2 Server Log Data (IP Addresses)

When you access our website, our servers automatically record your IP address in standard server logs.

| Data | Purpose | Legal Basis | Retention |
|---|---|---|---|
| IP address | Technical debugging and bot/abuse defence | Legitimate interest (Art. 6(1)(f) GDPR) — see below | 7 days, then automatically deleted |

**Our legitimate interest:** Maintaining the security, reliability, and integrity of our service requires the ability to detect and respond to technical faults and malicious traffic. IP address logging is standard practice for this purpose and is specifically recognised in Recital 49 of the GDPR. We have assessed that this interest is not overridden by your privacy rights, given the short retention period (7 days) and the fact that IP log data is **not linked to or associated with any user account or identity**.

You have the right to object to this processing on grounds relating to your particular situation (see Section 7).

### 2.3 Bot Detection During Sign-Up (Cloudflare Turnstile)

During the account registration process, we use **Cloudflare Turnstile**, a bot-detection service provided by Cloudflare, Inc., to protect our sign-up form from automated abuse.

| Data processed | Purpose | Legal Basis | Location of processing |
|---|---|---|---|
| Technical signals (IP address, TLS fingerprint, browser User-Agent header, and site key) | To distinguish human users from automated bots during sign-up | Legitimate interest (Art. 6(1)(f) GDPR) — protecting the integrity of our registration process | **United States** |

**Important:** We do **not** share any of your account information (username, email, password) with Cloudflare. Cloudflare processes only the technical signals listed above, acting as our **data processor**.

Because Cloudflare is based in the United States, your IP address and other technical signals may be transferred to and processed in the US. This transfer is safeguarded by **Standard Contractual Clauses (SCCs)** approved by the European Commission, which ensure that your personal data receives a level of protection consistent with EU standards. For more information, see [Cloudflare's GDPR Compliance](https://www.cloudflare.com/trust-hub/gdpr/) and [Cloudflare's Turnstile Privacy Policy](https://www.cloudflare.com/turnstile-privacy-policy/).

---

## 3. How We Store and Protect Your Data

All account data (username, email address, and password/passkey) is stored in **encrypted form**.

We use **Amazon Cognito**, a managed identity and authentication service provided by Amazon Web Services (AWS), to store and manage account credentials. Your data is held in Amazon's data centre located in **Stockholm, Sweden** (AWS region \`eu-north-1\`), which is within the European Economic Area (EEA). No transfer of your personal data outside the EEA takes place.

Once you are logged in, your **authentication token** is stored in your browser's **JavaScript local storage** to keep you authenticated during your session. This is a strictly necessary technical measure to maintain your authenticated state. You can clear this data at any time by using your browser's "Clear site data" function or by logging out.

---

## 4. Data Processors

We use the following third-party service providers (data processors) who may process personal data on our behalf:

| Processor | Purpose | Location | Transfer safeguards |
|---|---|---|---|
| Amazon Web Services (Amazon Cognito) | Secure storage of account credentials | Stockholm, Sweden (EU/EEA) | Not applicable (EU/EEA) |
| Cloudflare, Inc. (Turnstile) | Bot detection during sign-up | United States | Standard Contractual Clauses (SCCs) |

We do **not** share your personal data with any other third parties.

---

## 5. Data We Do Not Collect

For clarity, we confirm that we do **not**:

- Track your reading behaviour, browsing history, or activity on our site
- Use cookies for analytics or advertising purposes
- Use cookies for authentication (we use local storage instead; see Section 3)
- Display advertisements
- Use profiling or automated decision-making that produces legal or similarly significant effects
- Receive personal data about you from other services or data brokers
- Share your data with any third party other than the processors listed in Section 4

---

## 6. How Long We Keep Your Data

| Data | Retention Period |
|---|---|
| Account data (username, email, password/passkey) | For as long as your account is active. If you delete your account, your data will be deleted promptly. |
| Server log data (IP addresses) | 7 days, then automatically deleted |
| Authentication token (browser local storage) | Until you log out or clear your browser data; cleared automatically when you end your session |

---

## 7. Your Rights

Under the GDPR, you have the following rights regarding your personal data:

- **Right of access** — You may request a copy of the personal data we hold about you.
- **Right to rectification** — You may ask us to correct inaccurate or incomplete data.
- **Right to erasure ("right to be forgotten")** — You may ask us to delete your personal data, subject to certain conditions.
- **Right to restriction of processing** — You may ask us to restrict how we use your data in certain circumstances.
- **Right to data portability** — You may request your data in a structured, commonly used, machine-readable format.
- **Right to object** — You may object to processing based on our legitimate interests (Sections 2.2 and 2.3). We will stop processing unless we can demonstrate compelling legitimate grounds that override your interests.

To exercise any of these rights, please contact us at [${email}](mailto:${email}). We will respond within one month.

We do not charge a fee for exercising your rights. We may ask you to verify your identity before processing your request.

---

## 8. Right to Lodge a Complaint

If you believe we have not handled your personal data in accordance with the GDPR, you have the right to lodge a complaint with a data protection supervisory authority.

As we are established in Germany, the competent supervisory authority for our cross-border processing is:

**Die Bundesbeauftragte für den Datenschutz und die Informationsfreiheit (BfDI)**  
Graurheindorfer Str. 153  
53117 Bonn  
Germany  
Email: [poststelle@bfdi.bund.de](mailto:poststelle@bfdi.bund.de)  
Website: [www.bfdi.bund.de](https://www.bfdi.bund.de)

You may also contact the supervisory authority in your own country of residence or place of work, or where the alleged breach occurred.

---

## 9. Automated Decision-Making and Profiling

We do not use automated decision-making or profiling that produces legal effects or similarly significantly affects you.

---

## 10. Changes to This Privacy Notice

We may update this Privacy Notice from time to time. When we do, we will update the "Last updated" date at the top of this page. We encourage you to review this notice periodically. For significant changes, we will notify registered users by email.

---

## 11. Contact Us

For any questions, concerns, or requests relating to this Privacy Notice or your personal data:

**${publicationName}**  
${responsiblePerson}  
${addressMd}

Email: [${email}](mailto:${email})
`
}
